import { useState, useEffect, useRef, useCallback } from "react";

export function useAudioAnalyser(audioSrc) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [needsInteraction, setNeedsInteraction] = useState(false);

  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const frequencyDataRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize audio context and analyser
  const initAudio = useCallback(() => {
    if (isInitializedRef.current) return audioRef.current;

    try {
      const audio = new Audio(audioSrc);
      // Note: Don't set crossOrigin for same-origin audio - it taints the data
      audio.loop = true;
      audio.preload = "auto";
      audioRef.current = audio;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      frequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount);

      const source = audioContext.createMediaElementSource(audio);
      source.connect(analyser);
      analyser.connect(audioContext.destination);
      sourceRef.current = source;

      // Audio event listeners
      audio.addEventListener("play", () => {
        setIsPlaying(true);
        setNeedsInteraction(false);
      });

      audio.addEventListener("pause", () => {
        setIsPlaying(false);
      });

      audio.addEventListener("ended", () => {
        // Loop is set, but just in case
        audio.currentTime = 0;
        audio.play();
      });

      isInitializedRef.current = true;
      setIsReady(true);

      return audio;
    } catch (e) {
      console.warn("Audio initialization error:", e);
      return null;
    }
  }, [audioSrc]);

  // Play audio
  const play = useCallback(async () => {
    let audio = audioRef.current;
    if (!audio) {
      audio = initAudio();
    }
    if (!audio) return;

    // Resume audio context if suspended (browser autoplay policy)
    if (audioContextRef.current && audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch (e) {
        console.warn("AudioContext resume failed:", e);
      }
    }

    try {
      await audio.play();
      localStorage.removeItem("musicPlayerPaused");
      setNeedsInteraction(false);
    } catch (e) {
      console.warn("Audio play failed:", e);
      setNeedsInteraction(true);
    }
  }, [initAudio]);

  // Pause audio
  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    localStorage.setItem("musicPlayerPaused", "true");
  }, []);

  // Toggle play/pause
  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  // Initialize audio on mount (no autoplay)
  useEffect(() => {
    initAudio();
  }, [initAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    isPlaying,
    isReady,
    needsInteraction,
    play,
    pause,
    toggle,
    audioElement: audioRef.current,
  };
}
